/**
 * CustomTransitionEdge - React Flow custom edge component for transitions
 * Sprint-4: Added click handler and hover interactions (FR-010)
 */

import React, { useState, useCallback } from 'react';
import { EdgeProps, getSmoothStepPath, Position } from '@xyflow/react';
import { useTheme } from '@mui/material/styles';
import { EDGE_BUTTON, EDGE_STROKE_WIDTH, EDGE_PATH } from '@modules/reactFlow/constants/edgeConfig';
import { FunnelEdgeMetricLabel, type FunnelInsightEdgeMetric } from '@modules/funnel-insights';

export interface CustomTransitionEdgeData {
  rules?: Array<{
    key: string;
    operator: string;
    value: any;
  }>;
  transitionType: string;
  sourceActionKey?: string;
  /** Per-edge visual transition override (Route.transition) — drives the corner marker */
  transition?: { type?: string };
  onEdgeClick?: (edgeId: string, edgeData: any) => void;
  onAnalyticsEdgeClick?: (edgeId: string, metric: FunnelInsightEdgeMetric) => void;
  analyticsOverlay?: FunnelInsightEdgeMetric;
  /**
   * Spec D D1b: 'analytics' switches to volume-scaled read-only styling.
   * undefined = editor rendering, byte-identical to pre-D1b (AC-D1b-7).
   */
  mode?: 'analytics';
  /** Max transitionCount across the overlay — denominator for width scaling. */
  analyticsMaxTransitionCount?: number;
}

type AnalyticsFlowKind = 'forward' | 'back' | 'jump';

/** Dominant traversal direction of the edge's traffic (E1: back/jump distinct). */
function deriveFlowKind(metric?: FunnelInsightEdgeMetric): AnalyticsFlowKind {
  if (!metric) return 'forward';
  const { forwardTransitionCount: forward, backTransitionCount: back, jumpTransitionCount: jump } = metric;
  if (back > forward && back >= jump) return 'back';
  if (jump > forward) return 'jump';
  return 'forward';
}

/** 1.5px floor + up to 5.5px scaled by share of the busiest edge. */
function analyticsStrokeWidth(metric: FunnelInsightEdgeMetric | undefined, maxCount: number | undefined): number {
  if (!metric || !maxCount || maxCount <= 0) return 1.5;
  const share = Math.min(Math.max(metric.transitionCount / maxCount, 0), 1);
  return Math.round((1.5 + 5.5 * share) * 10) / 10;
}

export type CustomTransitionEdgeProps = EdgeProps & {
  data?: CustomTransitionEdgeData;
};

export const CustomTransitionEdge: React.FC<CustomTransitionEdgeProps> = (props) => {
  const { id, sourceX, sourceY, targetX, targetY, data, selected } = props;
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const hasConditions = data?.rules && data.rules.length > 0;
  const hasAnalytics = Boolean(data?.analyticsOverlay);
  // Per-edge visual transition override — marked with a corner dot on the ℹ
  // button, NEVER by recoloring the edge (stroke color already encodes
  // selected > hovered > conditions > analytics > default)
  const hasTransitionOverride = Boolean(data?.transition?.type);
  const isAnalyticsMode = data?.mode === 'analytics';
  const flowKind = deriveFlowKind(data?.analyticsOverlay);

  // Extract edge metadata for data attributes
  const sourceEntityId = data?.sourceEntityId || 'unknown';
  const targetEntityId = data?.targetEntityId || 'unknown';
  const sourceActionKey = data?.sourceActionKey || 'unknown';

  // Sprint-5: Handle double-click on edge to open Transition Editor
  const handleDoubleClick = useCallback(() => {
    if (data?.onEdgeClick) {
      data.onEdgeClick(id, data);
    }
  }, [id, data]);

  // Sprint-5: Handle click on info button (ℹ) to open Transition Editor
  const handleButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent edge selection
    if (data?.onEdgeClick) {
      data.onEdgeClick(id, data);
    }
  }, [id, data]);

  // Determine source/target positions based on node positions
  const sourcePosition = sourceX < targetX ? Position.Right : Position.Left;
  const targetPosition = sourceX < targetX ? Position.Left : Position.Right;

  // Generate edge path using getSmoothStepPath for node avoidance
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: EDGE_PATH.BORDER_RADIUS,
  });

  // Edge colors: less bright by default, brighter on hover
  const getEdgeColor = () => {
    if (isAnalyticsMode) {
      // Analytics mode: color encodes the dominant traversal direction (E1)
      if (flowKind === 'back') return theme.palette.warning.main;
      if (flowKind === 'jump') return theme.palette.info.main;
      return theme.palette.grey[600];
    }
    if (selected) {
      // Selected: darker gray (most visible)
      return theme.palette.grey[700];
    }
    if (isHovered) {
      // Hovered: brighter gray (more visible than default)
      return theme.palette.grey[600];
    }
    if (hasConditions) {
      // Has conditions: orange
      return theme.palette.warning.main;
    }
    if (hasAnalytics) {
      return theme.palette.success.main;
    }
    // Default: lighter gray (less visible)
    return theme.palette.grey[400];
  };

  // Button color: orange if has conditions, blue otherwise
  // Default state: lighter (less bright)
  const buttonColor = hasConditions
    ? theme.palette.warning.light  // Lighter orange by default
    : theme.palette.primary.light;  // Lighter blue by default

  // Hover state: darker (more bright)
  const buttonHoverColor = hasConditions
    ? theme.palette.warning.main   // Brighter orange on hover
    : theme.palette.primary.main;   // Brighter blue on hover

  const edgeStrokeWidth = isAnalyticsMode
    ? analyticsStrokeWidth(data?.analyticsOverlay, data?.analyticsMaxTransitionCount)
    : selected
      ? EDGE_STROKE_WIDTH.SELECTED
      : (isHovered ? EDGE_STROKE_WIDTH.HOVERED : EDGE_STROKE_WIDTH.DEFAULT);

  // Analytics mode: back-flow dashed, jumps dotted (E1 distinction)
  const edgeDashArray = isAnalyticsMode
    ? (flowKind === 'back' ? '8 5' : flowKind === 'jump' ? '2 5' : undefined)
    : undefined;

  return (
    <g
      data-testid={`edge-${id}`}
      data-source-id={sourceEntityId}
      data-target-id={targetEntityId}
      data-action-key={sourceActionKey}
      data-has-conditions={hasConditions}
      data-condition-count={data?.rules?.length || 0}
      data-has-transition={hasTransitionOverride}
      data-transition-type={data?.transition?.type ?? ''}
      data-entity-type="screen"
      data-mode={isAnalyticsMode ? 'analytics' : undefined}
      data-flow-kind={isAnalyticsMode ? flowKind : undefined}
      data-analytics-width={isAnalyticsMode ? edgeStrokeWidth : undefined}
    >
      {/* Sprint-5: Invisible wider path for easier double-clicking (10px click target) */}
      <path
        data-testid={`edge-path-${id}`}
        d={edgePath}
        stroke="transparent"
        strokeWidth={EDGE_PATH.CLICK_TARGET_WIDTH}
        fill="none"
        onDoubleClick={handleDoubleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'default' }}
      />

      {/* Visible edge with state-based styling */}
      <path
        d={edgePath}
        stroke={getEdgeColor()}
        strokeWidth={edgeStrokeWidth}
        strokeDasharray={edgeDashArray}
        fill="none"
        pointerEvents="none"
      />

      {/* Info button in the middle of edge (edit affordance — hidden in analytics mode) */}
      {!isAnalyticsMode && (
      <foreignObject
        x={labelX - EDGE_BUTTON.SIZE / 2}
        y={labelY - EDGE_BUTTON.SIZE / 2}
        width={EDGE_BUTTON.SIZE}
        height={EDGE_BUTTON.SIZE}
        style={{ overflow: 'visible' }}
      >
        <div
          data-testid={`edge-button-${id}`}
          onClick={handleButtonClick}
          style={{
            position: 'relative', // anchors the transition-override corner dot
            width: EDGE_BUTTON.SIZE,
            height: EDGE_BUTTON.SIZE,
            borderRadius: '50%',
            backgroundColor: buttonColor,
            border: `${EDGE_BUTTON.BORDER_WIDTH}px solid ${theme.palette.background.paper}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: theme.palette.primary.contrastText,
            fontSize: '8px',
            fontWeight: 'bold',
            boxShadow: theme.shadows[2],
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.2)';
            e.currentTarget.style.boxShadow = theme.shadows[4];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = theme.shadows[2];
          }}
        >
          {hasConditions ? data?.rules?.length : 'ℹ'}
          {hasTransitionOverride && (
            <span
              data-testid={`edge-transition-marker-${id}`}
              style={{
                position: 'absolute',
                right: -3,
                bottom: -3,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                border: `1.5px solid ${theme.palette.background.paper}`,
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      </foreignObject>
      )}

      {data?.analyticsOverlay && (
        <foreignObject
          x={labelX - 37}
          y={isAnalyticsMode ? labelY - 15 : labelY + EDGE_BUTTON.SIZE / 2 + 6}
          width={74}
          height={30}
          style={{ overflow: 'visible' }}
        >
          <FunnelEdgeMetricLabel
            metric={data.analyticsOverlay}
            detailed={isAnalyticsMode}
            onClick={() => data.onAnalyticsEdgeClick?.(id, data.analyticsOverlay!)}
          />
        </foreignObject>
      )}
    </g>
  );
};
